import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import Container from '@mui/material/Container'
import CircularProgress from '@mui/material/CircularProgress'

import { useContextHook } from '../store/context'
import { get, setPageTitle } from '../utils/service'
import { setCuppingRoastIds } from '../store/actions'
import CreateCuppingSessionForm from '../components/cupping/createCuppingSessionForm'
import { createCuppingSessionSample } from '../utils/cuppingUtils'
import { Log } from '../types/log'
import { CuppingSession, CuppingSessionSample } from '../types/cuppingSession'

const NewCuppingView = (): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { state, dispatch } = useContextHook()
  const location = useLocation()
  const locationState = location.state as { cuppingData: CuppingSession }

  const [isLoadingFinished, setIsLoadingFinished] = React.useState<boolean>(false)
  const [loadedLogUrls, setLoadedLogUrls] = React.useState<Array<{ url: string; errorCode: number }>>([])
  const [selectedRoasts, setSelectedRoasts] = React.useState<Array<CuppingSessionSample>>([])

  const isUrl = (roast: string): boolean => {
    return roast.substring(0, 4) === 'http'
  }

  const updateLoadedLogUrls = (roastId: string, errorCode: number): void => {
    setLoadedLogUrls((oldLoadedLogUrls) => {
      // if this log url does not exist, add it
      return oldLoadedLogUrls.find((x) => x.url === roastId)
        ? oldLoadedLogUrls
        : [...oldLoadedLogUrls, { url: roastId, errorCode: errorCode }]
    })
  }

  React.useEffect(() => {
    setPageTitle(t('cupping.createnewcuppingsession'))
  }, [t])

  React.useEffect(() => {
    if (!isLoadingFinished && state.cuppingRoastIds) {
      // create array with all selected roasts in correct order.
      // Log data will be loaded and added to roasts with log property, external roasts are added here
      setSelectedRoasts(
        state.cuppingRoastIds.map((x: string) => {
          return isUrl(x) ? createCuppingSessionSample({ url: x } as Log) : createCuppingSessionSample(null, x)
        })
      )
      state.cuppingRoastIds
        .filter((x: string) => isUrl(x))
        .forEach((roastId: string) => {
          get<Log>(roastId)
            .then((response) => {
              const sample = createCuppingSessionSample(response.data)
              setSelectedRoasts((oldSelectedRoasts) => {
                return oldSelectedRoasts.map((x) => (x.log === roastId ? sample : x))
              })
              updateLoadedLogUrls(roastId, 0)
            })
            .catch((error) => {
              setSelectedRoasts((oldSelectedRoasts) => {
                return oldSelectedRoasts.filter((x) => x.log !== roastId)
              })
              updateLoadedLogUrls(roastId, error.response.status)
            })
        })
    }
  }, [state.cuppingRoastIds, isLoadingFinished])

  React.useEffect(() => {
    if (state.cuppingRoastIds && loadedLogUrls.length === state.cuppingRoastIds.filter((x) => isUrl(x)).length) {
      setIsLoadingFinished(true)
    }
  }, [loadedLogUrls, state.cuppingRoastIds])

  const onCuppingSessionCreated = (cuppingSessionId: string): void => {
    // reset selected cupping logs
    dispatch(setCuppingRoastIds([]))
    navigate(`/cuppings/${cuppingSessionId}`)
  }

  const onSamplesChanged = React.useCallback(
    (samples: Array<string>): void => {
      dispatch(setCuppingRoastIds(samples))
    },
    [dispatch]
  )

  return (
    <Container component="main">
      {!isLoadingFinished && <CircularProgress />}
      {isLoadingFinished && (
        <CreateCuppingSessionForm
          samples={selectedRoasts}
          sessionName={locationState?.cuppingData?.name || ''}
          cuppingFormUrl={locationState?.cuppingData?.cupping_form || ''}
          isBlind={locationState?.cuppingData?.is_blind || false}
          customerUrl={state.currentCustomer?.url || ''}
          loadLogsErrors={loadedLogUrls.filter((x) => x.errorCode !== 0)}
          onCreateSuccess={onCuppingSessionCreated}
          onSamplesChanged={onSamplesChanged}
        />
      )}
    </Container>
  )
}

export default NewCuppingView
